// Import react libs
import { useContext, useEffect, useState } from 'react';

// Import style file
import {
    AboutWrapper,
    InfoSection,
    InfoCard,
    InfoTitle,
    InfoContent,
    SocialNetworkRow,
    SocialNetworkLink,
    SocialNetworkIcon,
    EditButton,
    EditTextArea,
    Icon,
} from './style';

// Import Global styles
import { Loader } from '../../utils/styles/Atoms';

// Import contexts
import { AuthContext } from '../../utils/contexts/AuthContext';

// Import hooks
import {
    useFetchContactsInfos,
    useFetchDeliveryInfos,
    useFetchSchedulesInfos,
    useUpdateContacts,
    useUpdateDelivery,
    useUpdateSchedules,
} from '../../utils/hooks/about';

// Import components
import { ErrorMessage, ErrorMessageStack } from '../../components/Error';

// Import lib
import {
    formatInstagram,
    formatWhatsApp,
} from '../../utils/libs/socialNetworkLinkFormatter';

// Import assets
import { ReactComponent as instagramIcon } from '../../assets/icons/instagram.svg';
import { ReactComponent as whatsappIcon } from '../../assets/icons/whatsapp.svg';

import { ReactComponent as modifyIcon } from '../../assets/icons/settings.svg';
import { ReactComponent as validateIcon } from '../../assets/icons/validate.svg';

// Import texts
import aboutTexts from '../../texts/about';
import errorTexts from '../../texts/error';

function About({ ICON_WIDTH = 30 }) {
    // Get the logged user
    const { isAdmin } = useContext(AuthContext);

    // Get about page infos from the cached query
    const {
        data: deliveryText,
        isLoading: isDeliveryLoading,
        isError: isDeliveryError,
        refetch: refetchDelivery,
    } = useFetchDeliveryInfos();
    const {
        data: contactsInfos,
        isLoading: isContactsLoading,
        isError: isContactsError,
        refetch: refetchContacts,
    } = useFetchContactsInfos();
    const {
        data: schedulesText,
        isLoading: isSchedulesLoading,
        isError: isSchedulesError,
        refetch: refetchSchedules,
    } = useFetchSchedulesInfos();

    // Get the update functions
    const {
        updateDelivery,
        isLoading: isUpdateDeliveryLoading,
        isError: isUpdateDeliveryError,
        resetError: resetUpdateDeliveryError,
    } = useUpdateDelivery();
    const {
        updateContacts,
        isLoading: isUpdateContactsLoading,
        isError: isUpdateContactsError,
        resetError: resetUpdateContactError,
    } = useUpdateContacts();
    const {
        updateSchedules,
        isLoading: isUpdateSchedulesLoading,
        isError: isUpdateSchedulesError,
        resetError: resetUpdateScheduleError,
    } = useUpdateSchedules();

    // Combine isLoading states and errors
    const isLoading =
        isDeliveryLoading ||
        isContactsLoading ||
        isSchedulesLoading ||
        isUpdateDeliveryLoading ||
        isUpdateContactsLoading ||
        isUpdateSchedulesLoading;

    const isFetchError = isDeliveryError || isContactsError || isSchedulesError;
    const isUpdateError =
        isUpdateDeliveryError ||
        isUpdateContactsError ||
        isUpdateSchedulesError;

    // Keep in a state the links to the socials networks
    const [instagramLink, setInstagramLink] = useState(null);
    const [whatsAppLink, setWhatsAppLink] = useState(null);

    // Keep a state to know if the admin is editing a field
    // and the texts of the fields
    const [isEditingDelivery, setIsEditingDelivery] = useState(false);
    const [editedDeliveryText, setEditedDeliveryText] = useState(null);
    const [isEditingContacts, setIsEditingContacts] = useState(false);
    const [editedContactsInfos, setEditedContactInfos] = useState(null);
    const [isEditingSchedules, setIsEditingSchedules] = useState(false);
    const [editedSchedulesText, setEditedSchedulesText] = useState(null);

    // Set error for empty string values
    const [isAboutInfoEmpty, setIsAboutInfoEmpty] = useState(false);

    // Reset all errors
    function resetErrors() {
        setIsAboutInfoEmpty(false);

        resetUpdateContactError();
        resetUpdateDeliveryError();
        resetUpdateScheduleError();
    }

    // Update socials links every time contact infos change
    useEffect(() => {
        if (
            contactsInfos &&
            contactsInfos.instagram &&
            contactsInfos.instagram !== '' &&
            contactsInfos.whats_app &&
            contactsInfos.whats_app !== ''
        ) {
            setInstagramLink(formatInstagram(contactsInfos.instagram));
            setWhatsAppLink(formatWhatsApp(contactsInfos.whats_app));
        }
    }, [contactsInfos]);

    // Update edit infos each time editing states change
    useEffect(() => {
        if (deliveryText && deliveryText !== '') {
            setEditedDeliveryText(deliveryText);
        }
    }, [deliveryText, isEditingDelivery]);
    useEffect(() => {
        if (
            contactsInfos &&
            contactsInfos.instagram &&
            contactsInfos.instagram !== '' &&
            contactsInfos.whats_app &&
            contactsInfos.whats_app !== ''
        ) {
            setEditedContactInfos({
                instagram: contactsInfos.instagram,
                whats_app: contactsInfos.whats_app,
            });
        }
    }, [contactsInfos, isEditingContacts]);
    useEffect(() => {
        if (schedulesText && schedulesText !== '') {
            setEditedSchedulesText(schedulesText);
        }
    }, [schedulesText, isEditingSchedules]);

    // Handler for validate Delivery editing
    async function handleDeliveryValidate() {
        // If the fiel is empty throw an error
        if (editedDeliveryText.trim() === '') {
            setIsAboutInfoEmpty(true);
            setIsEditingDelivery(false);
            return;
        }

        // If the text don't change do nothing
        if (editedDeliveryText === deliveryText) {
            setIsEditingDelivery(false);
            return;
        }

        // Update the delivery infos
        const result = await updateDelivery(editedDeliveryText);
        setIsEditingDelivery(false);

        // If the update was successful, update the data in cache
        if (result) {
            refetchDelivery();
        }
    }

    // Handler for validate contacts editing
    async function handleContactsValidate() {
        // If a field is empty throw an error
        if (
            editedContactsInfos.instagram.trim() === '' ||
            editedContactsInfos.whats_app.trim() === ''
        ) {
            setIsAboutInfoEmpty(true);
            setIsEditingContacts(false);
            return;
        }

        // If the text don't change do nothing
        if (
            editedContactsInfos.instagram === contactsInfos.instagram &&
            editedContactsInfos.whats_app === contactsInfos.whats_app
        ) {
            setIsEditingContacts(false);
            return;
        }

        // Update the delivery infos
        const result = await updateContacts(
            editedContactsInfos.instagram,
            editedContactsInfos.whats_app,
        );
        setIsEditingContacts(false);

        // If the update was successful, update the data in cache
        if (result) {
            refetchContacts();
        }
    }

    // Handler for validate schedules editing
    async function handleSchedulesValidate() {
        // If the fiel is empty throw an error
        if (editedSchedulesText.trim() === '') {
            setIsAboutInfoEmpty(true);
            setIsEditingSchedules(false);
            return;
        }

        // If the text don't change do nothing
        if (editedSchedulesText === schedulesText) {
            setIsEditingSchedules(false);
            return;
        }

        // Update the delivery infos
        const result = await updateSchedules(editedSchedulesText);
        setIsEditingSchedules(false);

        // If the update was successful, update the data in cache
        if (result) {
            refetchSchedules();
        }
    }

    // Return the loader while datas or loading
    if (isLoading) {
        return (
            <AboutWrapper>
                <Loader />
            </AboutWrapper>
        );
    }

    // Return the page loading error when datas are not correctly loaded
    if (isFetchError) {
        return (
            <AboutWrapper>
                <ErrorMessage
                    critical={true}
                    message={errorTexts.pageLoadingError}
                />
            </AboutWrapper>
        );
    }

    return (
        <AboutWrapper>
            <ErrorMessageStack
                errorMessages={[
                    isUpdateError && {
                        message: errorTexts.updateError,
                        critical: false,
                    },
                    isAboutInfoEmpty && {
                        message: errorTexts.aboutInfoEmptyError,
                        critical: false,
                    },
                ].filter(Boolean)}
            />
            <InfoSection>
                <InfoCard>
                    {isAdmin &&
                        (!isEditingDelivery ? (
                            <EditButton
                                onClick={() => {
                                    resetErrors();
                                    setIsEditingDelivery(true);
                                }}
                            >
                                <Icon as={modifyIcon} width={ICON_WIDTH} />
                            </EditButton>
                        ) : (
                            <EditButton
                                onClick={() => {
                                    resetErrors();
                                    handleDeliveryValidate();
                                }}
                            >
                                <Icon as={validateIcon} width={ICON_WIDTH} />
                            </EditButton>
                        ))}
                    <InfoTitle>{aboutTexts.deliveryTitle}</InfoTitle>
                    {!isEditingDelivery ? (
                        <InfoContent>{deliveryText}</InfoContent>
                    ) : (
                        <EditTextArea
                            placeholder={aboutTexts.deliveryPlaceholder}
                            value={editedDeliveryText}
                            onChange={(e) =>
                                setEditedDeliveryText(e.target.value)
                            }
                        />
                    )}
                </InfoCard>
                <InfoCard>
                    {isAdmin &&
                        (!isEditingContacts ? (
                            <EditButton
                                onClick={() => {
                                    resetErrors();
                                    setIsEditingContacts(true);
                                }}
                            >
                                <Icon as={modifyIcon} width={ICON_WIDTH} />
                            </EditButton>
                        ) : (
                            <EditButton
                                onClick={() => {
                                    resetErrors();
                                    handleContactsValidate();
                                }}
                            >
                                <Icon as={validateIcon} width={ICON_WIDTH} />
                            </EditButton>
                        ))}
                    <InfoTitle>{aboutTexts.contactTitle}</InfoTitle>
                    <SocialNetworkRow>
                        <SocialNetworkLink
                            title={aboutTexts.instagramIconTitle}
                            href={instagramLink}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <SocialNetworkIcon
                                as={instagramIcon}
                                width={ICON_WIDTH}
                            />
                        </SocialNetworkLink>
                        {!isEditingContacts ? (
                            <SocialNetworkLink
                                title={aboutTexts.instagramIconTitle}
                                href={instagramLink}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {contactsInfos.instagram}
                            </SocialNetworkLink>
                        ) : (
                            <EditTextArea
                                placeholder={aboutTexts.instagramPlaceholder}
                                value={editedContactsInfos.instagram}
                                onChange={(e) =>
                                    setEditedContactInfos((prev) => ({
                                        ...prev,
                                        instagram: e.target.value,
                                    }))
                                }
                                style={{ height: '50%' }}
                            />
                        )}
                    </SocialNetworkRow>
                    <SocialNetworkRow>
                        <SocialNetworkLink
                            title={aboutTexts.whatsappIconTitle}
                            href={whatsAppLink}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <SocialNetworkIcon
                                as={whatsappIcon}
                                width={ICON_WIDTH}
                            />
                        </SocialNetworkLink>
                        {!isEditingContacts ? (
                            <SocialNetworkLink
                                title={aboutTexts.whatsappIconTitle}
                                href={whatsAppLink}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {contactsInfos.whats_app}
                            </SocialNetworkLink>
                        ) : (
                            <EditTextArea
                                placeholder={aboutTexts.whatsAppPlaceholder}
                                value={editedContactsInfos.whats_app}
                                onChange={(e) =>
                                    setEditedContactInfos((prev) => ({
                                        ...prev,
                                        whats_app: e.target.value,
                                    }))
                                }
                                style={{ height: '50%' }}
                            />
                        )}
                    </SocialNetworkRow>
                </InfoCard>
                <InfoCard>
                    {isAdmin &&
                        (!isEditingSchedules ? (
                            <EditButton
                                onClick={() => {
                                    resetErrors();
                                    setIsEditingSchedules(true);
                                }}
                            >
                                <Icon as={modifyIcon} width={ICON_WIDTH} />
                            </EditButton>
                        ) : (
                            <EditButton
                                onClick={() => {
                                    resetErrors();
                                    handleSchedulesValidate();
                                }}
                            >
                                <Icon as={validateIcon} width={ICON_WIDTH} />
                            </EditButton>
                        ))}
                    <InfoTitle>{aboutTexts.schedulesTitle}</InfoTitle>
                    {!isEditingSchedules ? (
                        <InfoContent>{schedulesText}</InfoContent>
                    ) : (
                        <EditTextArea
                            placeholder={aboutTexts.schedulesPlaceholder}
                            value={editedSchedulesText}
                            onChange={(e) =>
                                setEditedSchedulesText(e.target.value)
                            }
                        />
                    )}
                </InfoCard>
            </InfoSection>
        </AboutWrapper>
    );
}

export default About;
